import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';

import { useDownloadCoachDetailedScores } from './useDownloadCoachDetailedScores';
import type { RawKaiCoachSection } from './kaiCoachTypes';
import styles from './CoachDetailedScoresDialog.module.css';

interface CoachDetailedScoresDialogProps {
  courseId: string;
  moduleId: string;
  moduleTitle: string;
  onOpenChange(nextOpen: boolean): void;
  open: boolean;
  section: RawKaiCoachSection;
}

export function CoachDetailedScoresDialog({
  courseId,
  moduleId,
  moduleTitle,
  onOpenChange,
  open,
  section,
}: CoachDetailedScoresDialogProps) {
  const mutation = useDownloadCoachDetailedScores();
  const [includeConversations, setIncludeConversations] = useState(false);

  // Reset the form only on the closed -> open transition so a prior run's choice
  // (or error) never lingers. The guard is essential: `mutation` is a fresh
  // object every render, so an unguarded effect would wipe the selection.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setIncludeConversations(false);
      mutation.reset();
    }
    wasOpen.current = open;
  }, [mutation, open]);

  async function handleDownload() {
    try {
      await mutation.mutateAsync({
        courseId,
        includeConversations,
        moduleId,
        moduleTitle,
        section,
      });
      onOpenChange(false);
    } catch {
      // Error surfaced below via mutation.isError.
    }
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>
              Download detailed scores
            </Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.description}>
                Exports a CSV with every student&rsquo;s participation score for
                each activity in this coach module.
              </p>
            </Dialog.Description>
          </div>

          <div className={styles.body}>
            <label className={styles.checkboxLabel}>
              <input
                checked={includeConversations}
                onChange={(event) =>
                  setIncludeConversations(event.target.checked)
                }
                type="checkbox"
              />
              <span className={styles.checkboxText}>
                <span className={styles.checkboxName}>
                  Include conversation history in the export
                </span>
                <span className={styles.checkboxHint}>
                  Adds a column per activity with each student&rsquo;s full
                  transcript. This can make the file much larger and slower to
                  prepare.
                </span>
              </span>
            </label>
          </div>

          {mutation.isError ? (
            <p aria-live="polite" className={styles.error} role="alert">
              Could not prepare the export. {mutation.error.message}
            </p>
          ) : null}

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button className={styles.cancelButton} variant="secondary">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              className={styles.downloadButton}
              disabled={mutation.isPending}
              onClick={() => {
                void handleDownload();
              }}
              variant="primary"
            >
              {mutation.isPending ? 'Preparing…' : 'Download'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
